/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { RowSelectionState } from "@tanstack/table-core";
import { Else, If, Then } from "react-if";
import { Box, FlexContainer, Spacer, theme } from "@nordcloud/gnui";
import { GroupsSortFields, SortOrder, Group } from "~/generated/graphql";
import { LoaderWrap } from "~/components/Loader";
import { NoData } from "~/components/NoData";
import { Pagination } from "~/components/Pagination";
import {
  TableSearchInput,
  getNoSearchResultsMessage,
  minSearchLength,
} from "~/components/SearchInput";
import { SortSelector } from "~/components/SortSelector";
import { TableWrapper } from "~/components/Tables";
import { TABLE_LABELS } from "~/constants";
import { useQueryState } from "~/hooks";
import { isEmpty, isNotNil } from "~/tools";
import { columns } from "~/views/resourceGroups/components/ResourceGroupsTableColumns";
import { useGetResourceGroups } from "~/views/resourceGroups/hooks";
import { ResourceGroupQuery } from "~/views/resourceGroups/types";
import { prepareResourceGroupsFilter } from "./resourceGroupFilterBuilders";
import { ResourceGroupFiltersSidebar } from "./ResourceGroupFiltersSidebar";

const sortOptions = [
  {
    value: GroupsSortFields.Name,
    label: "Resource Group Name",
  },
  {
    value: GroupsSortFields.CreatedAt,
    label: TABLE_LABELS.createdAt,
  },
  {
    value: GroupsSortFields.UpdatedAt,
    label: TABLE_LABELS.updatedAt,
  },
  {
    value: GroupsSortFields.UpdatedBy,
    label: TABLE_LABELS.updatedBy,
  },
  {
    value: GroupsSortFields.Type,
    label: TABLE_LABELS.type,
  },
  {
    value: GroupsSortFields.TotalResourcesCount,
    label: "Total Resources",
  },
  {
    value: GroupsSortFields.OnlineResourcesCount,
    label: "Online Resources",
  },
  {
    value: GroupsSortFields.OfflineResourcesCount,
    label: "Disconnected Resources",
  },
];

export function ResourceGroupsTable() {
  const { queryState, updateQueryState } = useQueryState<ResourceGroupQuery>();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const handleSortDirection = (order: SortOrder) => {
    updateQueryState({
      ...queryState,
      order,
    });
  };

  const handleSortField = (field: GroupsSortFields) => {
    updateQueryState({
      ...queryState,
      field,
    });
  };

  const handlePhraseField = (field: string | undefined) => {
    updateQueryState({
      ...queryState,
      searchPhrase: field,
    });
  };

  const isSearchAllowed =
    !queryState.searchPhrase ||
    queryState.searchPhrase.length >= minSearchLength;

  const { data, loading, count } = useGetResourceGroups({
    page: queryState.page,
    limit: queryState.limit,
    sort: {
      field: queryState.field ?? GroupsSortFields.Name,
      order: queryState.order ?? SortOrder.Asc,
    },
    filter: prepareResourceGroupsFilter(queryState),
    excludeResourceSelectors: true,
  });

  return (
    <>
      <Box boxStyle="lightGrey" spacing="spacing04">
        <FlexContainer justifyContent="space-between">
          <SortSelector<GroupsSortFields>
            options={sortOptions}
            value={queryState.field ?? GroupsSortFields.Name}
            direction={queryState.order ?? SortOrder.Asc}
            onSelect={handleSortField}
            onSortChange={handleSortDirection}
          />
          <Spacer width={theme.spacing.spacing04} />
          <TableSearchInput<ResourceGroupQuery>
            onSubmit={handlePhraseField}
            placeholder="Type a Keyword (e.g. Resource Group Name, Resource Group ID, etc.)"
          />
          <Spacer width={theme.spacing.spacing04} />
          <ResourceGroupFiltersSidebar resultsCount={count ?? 0} />
        </FlexContainer>
      </Box>
      <If condition={!isSearchAllowed}>
        <Then>
          <NoData hasIcon message={getNoSearchResultsMessage()} />
        </Then>
        <Else>
          <LoaderWrap loading={loading} inContent>
            <If condition={isEmpty(data)}>
              <Then>
                <NoData hasIcon message={"There are no Resource Groups."} />
              </Then>
              <Else>
                <>
                  <TableWrapper<Partial<Group>>
                    loading={loading}
                    data={data.filter(isNotNil) as Group[]}
                    columns={columns()}
                    rowSelection={rowSelection}
                    setRowSelection={setRowSelection}
                    stripped
                  />
                  <Pagination count={count} />
                </>
              </Else>
            </If>
          </LoaderWrap>
        </Else>
      </If>
    </>
  );
}
